import { type SceneEntity, seamComponent } from '@seddi/scene';

import { findWithGarment } from '../domain/scene/scene';
import type { SceneDesc } from '../domain/scene/sceneTypes';
import { type EntityRenderable, updateTransform } from '../renderables/entityRenderable';
import {
  attachGarmentGeometry,
  create as createGarment,
  detachGarmentGeometry,
} from '../renderables/garmentRenderable';
import { create as createLight } from '../renderables/lightRenderable';
import { create as createMesh } from '../renderables/meshRenderable';
import { create as createModel } from '../renderables/modelRenderable';
import { create as createPiece } from '../renderables/pieceRenderable';
import { create as createSeam } from '../renderables/seamRenderable';
import { update as updateGarment } from './garmentReconciler';
import { update as updateLight } from './lightReconciler';
import { update as updateMesh } from './meshReconciler';
import { update as updateMeshRenderable } from './meshRenderableReconciler';
import { update as updateModel } from './modelReconciler';
import { update1st as updatePiece1st, update2nd as updatePiece2nd } from './pieceReconciler';
import type { SceneReconcilerFrame, SceneReconcilerFrameEntity } from './sceneReconcilerFrame';
import { update as updateSeam } from './seamReconciler';

export const getGarmentGeometryIdFromEntity = (desc: SceneEntity): string => {
  // TODO: Use surface URL filepath as ID?.
  return desc._id;
};

/**
 * Update entity 1st pass.
 * @param renderable - Entity renderable.
 * @param nextProps Next entity state.
 * @param prevProps Prev entity state.
 */
export const update1st = (
  renderable: EntityRenderable,
  nextProps: SceneEntity,
  prevProps: SceneEntity | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  frame.nextParent = nextProps;
  frame.prevParent = prevProps;
  const {
    config,
    nextGarmentGeometry,
    nextUseGarmentGeometry,
    prevGarmentGeometry,
    prevUseGarmentGeometry,
    resources,
  } = frame;
  const nextComponents = nextProps.components;
  const prevComponents = prevProps?.components;
  let dirty = false;

  const entity = { data: nextProps } as SceneReconcilerFrameEntity;
  frame.entities.set(nextProps._id, entity);

  if (nextComponents !== prevComponents || nextUseGarmentGeometry !== prevUseGarmentGeometry) {
    // Reconcile garment geometry.
    if (nextComponents.garmentGeometry) {
      const nextComponent = nextComponents.garmentGeometry;
      const prevComponent = prevComponents?.garmentGeometry;

      if (nextComponent !== prevComponent || nextUseGarmentGeometry !== prevUseGarmentGeometry) {
        renderable.garment ??= createGarment();
        const { garment } = renderable;

        // Reconcile surface URL.
        if (config.garmentGeometryResource) {
          const nextSurfaceUrl = nextUseGarmentGeometry && nextComponent?.surfaceUrl;
          const prevSurfaceUrl = prevUseGarmentGeometry && prevComponent?.surfaceUrl;

          if (nextSurfaceUrl !== prevSurfaceUrl) {
            // Detach garment geometry resource.
            if (prevSurfaceUrl) {
              detachGarmentGeometry(garment, resources);
            }
            // Attach garment geometry resource.
            if (nextSurfaceUrl) {
              // TODO: Use surface URL filepath as ID?.
              const resourceId = nextProps._id;
              const resource =
                frame.nextGarmentGeometry ?? resources.findGarmentGeometry(resourceId);
              if (resource) {
                attachGarmentGeometry(garment, resourceId, resources);
              } else if (!resources.hasGarmentGeometryRequest(resourceId)) {
                resources.loadGarmentGeometryDeferred({
                  children: frame.nextScene!.children,
                  id: resourceId,
                  url: nextComponent.surfaceUrl,
                });
              }
            }
          }
        }

        // Reconcile stitches textures URL.
        {
          const nextStitchesUrl0 =
            nextUseGarmentGeometry && (nextGarmentGeometry as any)?.stitchesTexturesUrl;
          const nextStitchesUrl1 =
            nextUseGarmentGeometry && (nextGarmentGeometry as any)?.stitchesTexturesBinaryUrl;
          config.useStitchesDecals =
            config.useStitchesDecals && Boolean(nextStitchesUrl0) && Boolean(nextStitchesUrl1);

          if (config.useStitchesDecals && nextGarmentGeometry !== prevGarmentGeometry) {
            // Load stitches textures.
            const id = nextProps._id;
            const resource = resources.findStitchesTextures(id);
            if (resource) {
              frame.stitchesTextures = resource;
            } else if (!resources.hasStitchesTexturesRequest(id)) {
              const desc = {
                id,
                url: (nextGarmentGeometry as any).stitchesTexturesUrl,
                urlBinary: (nextGarmentGeometry as any).stitchesTexturesBinaryUrl,
              };
              resources.loadStitchesTexturesDeferred(desc);
            }
          }
        }
      }
    }
  }

  if (nextComponents !== prevComponents || nextGarmentGeometry !== prevGarmentGeometry) {
    // Reconcile garment piece.
    if (nextComponents.garmentPiece) {
      const nextComponent = nextComponents.garmentPiece;
      const prevComponent = prevComponents?.garmentPiece;

      if (nextComponent !== prevComponent || nextGarmentGeometry !== prevGarmentGeometry) {
        entity.isPiece = true;

        let piece = renderable.piece;
        if (!piece) {
          piece = createPiece(renderable.id, renderable.node);
          renderable.piece = piece;
          piece.mesh.frustumCulled = frame.config.frustumCulling;
        }
        dirty = updatePiece1st(piece, nextComponent, prevComponent, frame) || dirty;
      }
    }
  }

  if (nextComponents !== prevComponents) {
    // Reconcile garment updates.
    if (prevComponents?.garment !== nextComponents.garment) {
      renderable.garment ??= createGarment();

      updateGarment(renderable.garment, nextComponents.garment, prevComponents?.garment, frame);
      dirty = true;
    }

    // Reconcile transform 3D updates.
    if (prevComponents?.transform3D !== nextComponents.transform3D) {
      updateTransform(renderable, nextComponents.transform3D);
      dirty = true;
    }

    // Reconcile light updates.
    if (nextComponents.light !== prevComponents?.light) {
      const light =
        renderable.light ?? createLight(renderable.id, renderable.node, nextComponents.light.kind);
      renderable.light = light;
      dirty = updateLight(light, nextComponents.light) || dirty;
    }

    // Reconcile mesh updates.
    if (nextComponents.mesh !== prevComponents?.mesh) {
      let mesh = renderable.mesh;
      if (!mesh) {
        mesh = createMesh(renderable.id, renderable.node, nextProps.name);
        mesh.mesh.frustumCulled = frame.config.frustumCulling;
        renderable.mesh = mesh;
      }
      dirty = updateMesh(mesh, nextComponents.mesh, prevComponents?.mesh, frame) || dirty;
    }

    // Reconcile mesh renderable updates.
    if (nextComponents.meshRenderable !== prevComponents?.meshRenderable) {
      const mesh = renderable.mesh ?? createMesh(renderable.id, renderable.node, nextProps.name);
      renderable.mesh = mesh;
      dirty =
        updateMeshRenderable(
          mesh,
          nextComponents.meshRenderable,
          prevComponents?.meshRenderable,
          frame,
        ) || dirty;
    }

    // Reconcile model updates.
    if (nextComponents.model !== prevComponents?.model) {
      let model = renderable.model;
      if (!model) {
        model = createModel(renderable.id, renderable.node);
        renderable.model = model;
      }
      dirty = updateModel(model, nextComponents.model, prevComponents?.model, frame) || dirty;
    }
  }

  // Update frame.
  frame.nextParent = undefined;
  frame.prevParent = undefined;
  // Update renderable cache.
  renderable.node.userData = nextComponents;

  return dirty;
};

/**
 * Update entity 2nd pass.
 * @param renderable - Entity renderable.
 * @param nextProps - Next entity state.
 * @param prevProps - Prev entity state.
 * @param frame - Scene reconciler frame.
 */
export const update2nd = (() => {
  const _pieces = <string[]>[];

  return (
    renderable: EntityRenderable,
    nextProps: SceneEntity,
    prevProps: SceneEntity | undefined,
    frame: SceneReconcilerFrame,
  ): boolean => {
    // Update frame.
    const nextScene = frame.nextScene;
    const prevScene = frame.prevScene;
    frame.nextParent = nextProps;
    frame.prevParent = prevProps;

    const nextComponents = nextProps.components;
    const prevComponents = prevProps?.components;
    let dirty = false;

    // buttons placement depends on all pieces visited first (holes)
    if (nextComponents.garmentPiece) {
      // update trigger restrictions?
      const piece = renderable.piece;
      dirty =
        updatePiece2nd(piece, nextComponents.garmentPiece, prevComponents?.garmentPiece, frame) ||
        dirty;
    }

    // do seam
    if (nextComponents.garmentSeam) {
      // geometry update triggered by piece geometry updates
      let isGeometryDirty =
        !prevScene || !nextScene ? false : nextScene.garmentGeometry !== prevScene.garmentGeometry;

      if (!isGeometryDirty) {
        _pieces.length = 0;
        seamComponent.Pieces(nextComponents.garmentSeam, _pieces);
        isGeometryDirty = _pieces.some((piece) => frame.entities.get(piece)?.isGeometryDirty);
      }

      frame.entities.get(nextProps._id).isGeometryDirty = isGeometryDirty;

      if (nextComponents.garmentSeam !== prevComponents?.garmentSeam || isGeometryDirty) {
        let seam = renderable.seam;
        if (!seam) {
          seam = createSeam(
            renderable.id,
            renderable.node,
            nextComponents.garmentSeam,
            frame.theme,
            frame.config.useStitchesDecals,
          );
          seam.mesh.frustumCulled = frame.config.frustumCulling;
          renderable.seam = seam;
        }
        dirty =
          updateSeam(seam, nextComponents.garmentSeam, prevComponents?.garmentSeam, frame) || dirty;
      }
    }

    // Update frame cleanup.
    frame.nextParent = undefined;
    frame.prevParent = undefined;

    return dirty;
  };
})();
